import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import GoogleMaps from 'components/common/GoogleMaps';
import { Center, InfoWindowPosition, MarkerData } from 'components/common/GoogleMaps/GoogleMaps.types';
import { Store } from 'components/common/types/Store.types';
import { getIsReseller } from 'store/publisher/selectors';

import FrameInfo from './FrameInfo';
import FrameInfoConfig from './FrameInfo/FrameInfo.types';

type AssetsMapAsset = {
  frameId: number | string;
  impressions?: number;
  latitude: number;
  longitude: number;
  mediaOwner?: string;
};

type AssetsMapProps = {
  assets?: AssetsMapAsset[];
};

type AssetsMapMarker = MarkerData & AssetsMapAsset;

const DEFAULT_ASSETS: AssetsMapAsset[] = [];

const AssetsMap = ({ assets = DEFAULT_ASSETS }: AssetsMapProps): React.ReactElement => {
  const { latitude, longitude } = useSelector((state: Store) => state.publisher.configuration);
  const [markerData, setMarkerData] = useState<AssetsMapMarker[]>([]);
  const [mapCenter, setMapCenter] = useState<Center>({
    lat: latitude,
    lng: longitude,
  });
  const [infoWindowPosition, setInfoWindowPosition] = useState<InfoWindowPosition | null>(null);
  const [frameInfo, setFrameInfo] = useState<FrameInfoConfig>({});
  const isReseller = useSelector(getIsReseller);

  useEffect(() => {
    setMarkerData(
      assets.map((item) => ({
        ...item,
        id: item.frameId,
        lat: item.latitude,
        lng: item.longitude,
      })),
    );
    setMapCenter({
      lat: assets[0]?.latitude ?? latitude,
      lng: assets[0]?.longitude ?? longitude,
    });
  }, [assets]);

  const onMarkerMouseOver = (frame: MarkerData): void => {
    const { frameId, impressions, lat, lng, mediaOwner } = frame as AssetsMapMarker;

    setFrameInfo({ frameId, impressions, mediaOwner });
    setInfoWindowPosition({ lat, lng });
  };

  const onMarkerMouseOut = (): void => {
    setFrameInfo({});
    setInfoWindowPosition(null);
  };

  return (
    <GoogleMaps
      key={JSON.stringify(markerData)}
      markerData={markerData}
      center={mapCenter}
      infoWindowComponent={<FrameInfo config={frameInfo} isReseller={isReseller} />}
      onMarkerMouseOver={onMarkerMouseOver}
      onMarkerMouseOut={onMarkerMouseOut}
      infoWindowPosition={infoWindowPosition}
    />
  );
};

export default AssetsMap;
